"use client";
import type { GPOnboardingStepProps } from "./utils";

import { AppleLogo } from "@/components/icons/AppleLogo";
import { FacebookLogo } from "@/components/icons/FacebookLogo";
import { GoogleLogo } from "@/components/icons/GoogleLogo";
import { Input } from "@/components/ui/input";
import { type LoginMethod, usePassportProvider } from "@/context";
import { Action } from "@/context/AnalyticsProvider";
import { GamePageSection } from "@/context/AnalyticsProvider/gamePage";
import { useLoginModal } from "@/context/LoginModalContext";
import { cn } from "@/lib/utils";
import { inFacebookBrowser, isTikTokBrowser } from "@/utils/browser";
import { redirectGameToExternalBrowser } from "@/utils/loginredirect";
import { notifyError } from "@/utils/monitoring";
import MarketingConsent from "@components/MarketingConsent";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  LoaderCircle,
  MailIcon,
  TriangleAlert,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { z } from "zod";
import { GlassButton } from "../GlassButton";
import { TermsAndPrivacy } from "./TermsAndPrivacy";

const SOCIAL_LOGINS: LoginMethod[] = ["google", "apple", "facebook"];

// create looping through array of defined login methods that returns a ReactNode for the icon
const getLoginIcon = (method: LoginMethod) => {
  if (method === "google") return <GoogleLogo className="size-4 md:size-5" />;
  if (method === "apple") return <AppleLogo className="size-5 md:size-6" />;
  if (method === "facebook")
    return <FacebookLogo className="size-5 md:size-6" />;
};

export const GPOnboardingLogin = ({
  onNext,
  isVisible,
  track,
}: GPOnboardingStepProps) => {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [showEmail, setShowEmail] = useState(false);

  const { loginDirectly, isLoggingIn, isLoggedIn } = usePassportProvider();
  const { setDisableLoadingModal } = useLoginModal();

  const handleLogin = useCallback(
    async (loginMethod: LoginMethod) => {
      // Redirect to external browser if in fb browser or tiktok browser
      if (inFacebookBrowser() || isTikTokBrowser()) {
        const redirected = redirectGameToExternalBrowser(false, undefined, {
          loginMethod:
            typeof loginMethod === "string"
              ? loginMethod
              : JSON.stringify(loginMethod),
          loginRedirect: "true",
        });
        if (redirected) return;
      }

      setDisableLoadingModal(true);
      const isSocialLogin = typeof loginMethod === "string";
      const control = (
        isSocialLogin ? `social-login-${loginMethod}` : "email-login"
      ) as Lowercase<string>;

      try {
        await loginDirectly(loginMethod);
        track({
          action: Action.SUCCEEDED,
          extras: {
            control,
            section: GamePageSection.ONBOARDING_MODAL,
          },
        });
      } catch (error) {
        track({
          action: Action.FAILED,
          extras: {
            control,
            section: GamePageSection.ONBOARDING_MODAL,
          },
        });
        notifyError(error, "login");
      } finally {
        setDisableLoadingModal(false);
      }
    },
    [loginDirectly, track, setDisableLoadingModal],
  );

  const handleEmailSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!email.trim()) {
        setEmailError("Email is required");
        return;
      }
      if (z.string().email().safeParse(email).error) {
        setEmailError("Invalid email address");
        return;
      }
      setEmailError(null);
      await handleLogin({ email });
    },
    [email, handleLogin],
  );

  useEffect(() => {
    if (isLoggedIn && isVisible) onNext();
  }, [isLoggedIn, onNext, isVisible]);

  const handleOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setEmail(e.target.value);
      setEmailError(null);
    },
    [],
  );

  const handleCloseEmail = useCallback(() => {
    setShowEmail(false);
    setEmailError(null);
  }, []);

  return (
    <div className="flex h-full w-full flex-col items-center justify-between">
      {/* Progress bar with Motivation text */}
      <div>
        <p className="text-[clamp(1.25rem,6.25vw,1.875rem)] font-bold leading-[clamp(1.5rem,6.25vw,2.25rem)]">
          Sign in to claim your reward
        </p>
      </div>
      <div className="relative size-full">
        {/* Logging in */}
        <div
          className={cn(
            "absolute top-1/2 left-1/2 -translate-x-1/2 px-6 py-10 space-y-8 flex flex-col items-center transition-all duration-200 ease-in-out",
            isLoggingIn || isLoggedIn
              ? "opacity-100 -translate-y-1/2 pointer-events-auto"
              : "opacity-0 translate-y-10 pointer-events-none",
          )}
        >
          <LoaderCircle className="size-6 animate-spin" />
          <div className="text-center space-y-2">
            <p className="font-bold">Signing in</p>
            <p className="text-sm text-muted-foreground whitespace-nowrap">
              Continue in the popup window, then return.
            </p>
          </div>
        </div>
        {/* Sign in Options */}
        <div
          className={cn(
            "relative top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 transition-all duration-200 ease-in-out",
            isLoggingIn || isLoggedIn
              ? "opacity-0 translate-y-10 pointer-events-none"
              : "opacity-100 -translate-y-1/2 pointer-events-auto",
          )}
        >
          <div
            className={cn(
              "flex w-full justify-evenly absolute top-1/2 left-1/2 -translate-y-1/2 transition-all duration-300 ease-in-out",
              showEmail
                ? "opacity-0 -translate-x-full pointer-events-none"
                : "opacity-100 -translate-x-1/2 pointer-events-auto",
            )}
          >
            {SOCIAL_LOGINS.map((method) => (
              <GlassButton
                key={method as string}
                control={`social-login-${method}`}
                section={GamePageSection.ONBOARDING_MODAL}
                className="size-12 md:size-16"
                onClick={() => handleLogin(method)}
              >
                {getLoginIcon(method)}
              </GlassButton>
            ))}

            <GlassButton
              key="email"
              control="email-login"
              section={GamePageSection.ONBOARDING_MODAL}
              className="size-12 md:size-16"
              onClick={() => setShowEmail(true)}
            >
              <MailIcon className="size-5 md:size-6" />
            </GlassButton>
          </div>

          <form
            onSubmit={handleEmailSubmit}
            className={cn(
              "w-full absolute top-1/2 left-1/2 -translate-y-1/2 transition-all duration-300 ease-in-out",
              showEmail
                ? "opacity-100 -translate-x-1/2 pointer-events-auto"
                : "opacity-0 translate-x-full pointer-events-none",
            )}
            noValidate
          >
            <div className="flex items-center gap-x-2 w-full relative">
              <button
                type="button"
                className="shrink-0 size-7 sm:size-8 rounded-full bg-[rgba(243,243,243,0.08)] flex items-center justify-center hover:bg-[rgba(243,243,243,0.12)] transition-colors"
                aria-label="Back to login options"
                onClick={handleCloseEmail}
              >
                <ArrowLeftIcon className="size-5 text-[#f3f3f3]" />
              </button>
              <div className="bg-[rgba(243,243,243,0.04)] border border-[rgba(243,243,243,0.16)] rounded-2xl grow flex items-center gap-2 px-2 sm:px-3 py-1 sm:py-2 focus-within:border-[rgba(243,243,243,0.5)] transition-colors">
                <MailIcon className="size-4 sm:size-6 shrink-0 text-muted-foreground" />
                <Input
                  type="email"
                  inputMode="email"
                  placeholder="Enter email"
                  value={email}
                  onChange={handleOnChange}
                  className="flex-1 bg-transparent px-2 border-0 text-base text-muted-foreground placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0"
                  autoComplete="email"
                />
                <button
                  type="submit"
                  className="shrink-0 size-7 sm:size-8 rounded-full bg-[rgba(243,243,243,0.08)] flex items-center justify-center hover:bg-[rgba(243,243,243,0.12)] transition-colors"
                  aria-label="Submit email"
                >
                  <ArrowRightIcon className="size-5 text-[#f3f3f3]" />
                </button>
              </div>
              <p
                className={cn(
                  "text-sm text-red-500 h-5 flex items-center gap-x-2 left-10 top-full mt-2 absolute transition-opacity ease-in-out",
                  emailError ? "opacity-100" : "opacity-0",
                )}
              >
                <TriangleAlert className="size-4" /> {emailError}
              </p>
            </div>
          </form>
        </div>
      </div>
      {/* Rules & Privacy & Marketing Consent */}
      <div className="flex flex-col w-full">
        <TermsAndPrivacy track={track} />
        <MarketingConsent />
      </div>
    </div>
  );
};
